import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Select, Skeleton, Divider, Row, Col, InputNumber } from 'antd';
import FormModal from '../../../shared/components/modal/FormModal';
import { FetchFileByteRequest, FetchFileByteResponse, FileByteDetail } from '../../../shared/models/file.profile.model';
import { AcceptedFileTypes, APPLICATION_ID, Base64Image, FETCH_FILE_BYTES, GUTTOR_SPACE_SMALL } from '../../../shared/constants/commonConstants';
import SendRequest from '../../../shared/services/sendRequest.service';
import Slider from 'react-slick';
import Upload, { UploadChangeParam } from 'antd/lib/upload';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAmenityMaster } from '../../../store/actions/hospitalAction';
import { RoomDetail } from '../../../shared/models/room.model';
import { error } from '../../../utils/utility';
import { ReactComponent as UploadSMIcon } from '../../../assets/images/svg/i_upload_sm.svg';

const EditRoom = ({ room, visible, onCancel, title, onRoomSave, roomType,
  onRoomtypeChange, setSelectedPhotos, existingRooms, disabled }: any): ReactElement => {
  const [selectedAmenities, setSelectedAmenities] = useState([] as any);
  const ref = useRef({ roomDetail: room });
  const [form] = Form.useForm();
  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };
  const [previewList, setPreviewList] = useState([] as string[]);
  const [isAPICall, setIsAPICall] = useState(true);
  const [files, updateFiles] = useState([] as any);
  const amenityMasterList = useSelector(
    ({ hospital: { amenityMaster } }: any): any => amenityMaster,
  );
  const dispatch = useDispatch();
  useEffect((): void => {
    if (!amenityMasterList) {
      dispatch(fetchAmenityMaster());
    }
  }, []);
  useEffect((): void => {
    if (ref.current.roomDetail) {
      form.setFieldsValue({
        roomAlias: ref.current.roomDetail.roomAlias,
        typeName: ref.current.roomDetail.typeName,
        roomCount: ref.current.roomDetail.roomCount,
        bedsCountPerRoom: ref.current.roomDetail.bedsCountPerRoom,
        selectedAmenities:
          ref.current.roomDetail.amenities?.filter((x: any): boolean => x.isActive).map((x: any): string => x.amenityName || x.value),
      });
      setSelectedAmenities(
        ref.current.roomDetail.amenities?.filter((x: any): boolean => x.isActive).map((x: any): string => x.amenityName || x.value));
    } else {
      form.resetFields();
    }
  }, []);

  useEffect((): void => {
    if (room) {
      ref.current.roomDetail = room;
      setIsAPICall(true);
      if (room.files && room.files.length > 0) {
        const fileIdentifiers: string[] = room.files
          .map((item: any): string => {
            return item?.fileId;
          });
        addPhotosToPreviewList(fileIdentifiers);
      } else {
        setIsAPICall(false);
      }
    } else {
      ref.current.roomDetail = null;
      setIsAPICall(false);
    }
  }, [room]);

  const addPhotosToPreviewList = async (
    fileIdentifiers: string[],
  ): Promise<void> => {
    if (fileIdentifiers.length > 0) {
      const payload: FetchFileByteRequest = {
        applicationId: APPLICATION_ID,
        fileIdentifiers,
      };
      setIsAPICall(true);
      const url = `${process.env.REACT_APP_BASE_URL}${FETCH_FILE_BYTES}`;
      const response: any = await SendRequest.instance.post<FetchFileByteRequest,
        FetchFileByteResponse>(url, payload);
      if (response && response.isSuccess && response.fileDetails && response.fileDetails.length > 0) {
        setIsAPICall(false);
        setPreviewList(response.fileDetails.map(
          (item: FileByteDetail): any => {
            return { src: Base64Image.concat(item?.fileBytes), file: item };
          },
        ));
      }
    }
  };
  const infinite = previewList.length > 4;
  const sliderSettings = {
    dots: false,
    infinite,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    autoplay: false,
    autoplaySpeed: 2000,
    cssEase: 'linear',
  };

  const getPreviewList = (): any => {
    if (previewList != null && previewList.length > 0) {
      return previewList.map(
        (item: any, id: number): ReactNode => {
          return (
            <div key={id} className="slider-item">
              <img
                src={item.src}
                alt={item.src}
              />
              <div className="delete-image" onClick={(e: any): void => { e.stopPropagation(); onSliderImageClick(item); }}>
                <span>Delete</span>
              </div>
            </div>
          );
        },
      );
    }
  };
  const f: any[] = [];
  const fileUploadHandler = {
    name: 'file',
    multiple: true,
    beforeUpload: (): any => false,
    files,
    showUploadList: false,
    onChange: (filesDa: UploadChangeParam): any => {
      const { file: newFile } = filesDa;
      f.push(newFile);
      const validFiles = [...f];
      if (validFiles && validFiles.length > 0) {
        const objectsUrls: any[] = [];
        validFiles.forEach((file: any): void => {
          objectsUrls.push({ src: URL.createObjectURL(file), file });
        });
        setPreviewList([...previewList, ...objectsUrls]);
        updateFiles([
          ...files,
          ...validFiles,
        ]);
      }
    },
  };
  const onSave = (): void => {
    const existingRoom: any[] = existingRooms && existingRooms.length ? [...existingRooms] : [];
    const fieldsValue: any = form.getFieldsValue();
    if (existingRoom && existingRoom.length) {
      const duplicateRoom = existingRoom.find(
        (v: RoomDetail): any => (v.roomAlias === fieldsValue.roomAlias) && (v.typeName === fieldsValue.typeName) && (v.indx !== room.indx));
      if (duplicateRoom) {
        error('Same room alias for the selected room type is already configured');
        return;
      }
    }
    const tempRoomDetails = { ...ref.current.roomDetail };
    const tempFiles = tempRoomDetails?.files ? [...tempRoomDetails?.files] : [];

    const inActiveFiles = tempFiles?.filter((x: any): boolean => {
      return previewList?.findIndex((y: any): boolean => y?.file?.fileGuid === x?.fileId) === -1;
    })?.map((z: any): any => ({ ...z, fileId: z?.fileId, isActive: false }));

    const activeFiles = previewList && previewList.length ?
      previewList.map((x: any): any => (x?.file?.fileGuid ?
        { file: x.file, fileId: x?.file?.fileGuid, isActive: true } :
        { file: x.file, isActive: true })) : [];

    tempRoomDetails.files = [...inActiveFiles, ...activeFiles];
    ref.current.roomDetail = tempRoomDetails;
    setSelectedPhotos(
      {
        id: ref.current.roomDetail ? ref.current.roomDetail.indx : 0,
        photo: (files && files.length) ?
          getFiles(files) : (ref.current.roomDetail?.files && ref.current.roomDetail?.files?.length) ?
            getFiles(ref.current.roomDetail.files) : [],
      });
    setTimeout((): void => {
      onRoomSave(ref.current.roomDetail ? { ...ref.current.roomDetail, ...fieldsValue } : form.getFieldsValue());
      ref.current.roomDetail = null;
      setPreviewList([] as string[]);
      updateFiles([] as any);
    }, 300);
  };

  const onSliderImageClick = (item: any): any => {
    const r = { ...ref.current.roomDetail };
    const p = [...previewList];
    let remaningFiles: any = [];
    const existingFile: any = r.files.find((v: any): any => v.fileId === item.file?.fileGuid);
    if (existingFile) {
      remaningFiles = p.filter((v: any): any => v?.file?.fileGuid !== item?.file?.fileGuid);
      setPreviewList([...remaningFiles]);
    } else {
      remaningFiles = p.filter((v: any): any => v?.file?.uid !== item?.file?.uid);
      setPreviewList([...remaningFiles]);
    }
  };

  const getFiles = (images: any[]): any => {
    if (previewList && previewList.length) {
      const tempFiles = previewList.map((v: any): any => {
        if (v.file.fileGuid) {
          return { fileId: v.file.fileGuid, fileName: v.file.fileName, isActive: true };
        } else {
          return { ...v };
        }
      });
      return [...tempFiles];
    } else if (images && images.length) {
      return [...images];
    } else {
      return [];
    }
  };

  const getAllAmenities = (): string[] => {
    return amenityMasterList
      .filter((x: any): boolean => x.isActive)
      .map((x: any): any => {
        return x.value;
      });
  };
  const onModalCancel = (): void => {
    setPreviewList([] as string[]);
    updateFiles([] as any);
    onCancel();
  };
  const onAmenityCheckBoxChange = (checkedList: any): void => {
    if (ref.current.roomDetail && ref.current.roomDetail?.amenities) {
      const inactiveAmenities = ref.current.roomDetail?.amenities
        ?.filter((x: any): any => !checkedList?.includes(x.amenityName || x.value) && x.isActive);
      inactiveAmenities?.forEach((x: any): void => {
        x.isActive = false;
      });
      const activeAmenities = checkedList?.map((x: string): any => {
        return {
          amenityName: x,
          amenityId: amenityMasterList.find((y: any): boolean => y.value === x)?.id,
          isActive: true,
        };
      });
      const mergedArray = [...inactiveAmenities, ...activeAmenities, ...ref.current.roomDetail?.amenities];
      const set = new Set();
      const unionArray = mergedArray.filter((item: any): any => {
        if (!set.has(item.amenityId)) {
          set.add(item.amenityId);
          return true;
        }
        return false;
      }, set);
      setSelectedAmenities(unionArray.map((v: any): any => v.amenityName || v.value));
      ref.current.roomDetail = { ...ref.current.roomDetail, amenities: unionArray };
    } else {
      return;
    }
  };
  return (
    <>
      <FormModal visible={visible} onCancel={onModalCancel} title={title} width={600} className="rooms-photos-modal" isFooter={false}>
        <Form {...layout} form={form} onFinish={onSave}>
          <p className="label-title">Upload room images</p>
          {isAPICall ? (
            <Skeleton active={true} paragraph={{ rows: 3 }} />
          ) : (
            previewList &&
            <Slider className="room-photos-slider mb-md" {...sliderSettings}> {getPreviewList()}</Slider>
          )}
          {!disabled &&
            <Upload disabled={disabled} {...fileUploadHandler} accept={AcceptedFileTypes}>
              <Button icon={<UploadSMIcon className="fill-gray v-align-middle" />}>&nbsp; Upload images</Button>
            </Upload>
          }
          <Divider />
          <Row gutter={GUTTOR_SPACE_SMALL}>
            <Col md={10} className="edit-room-left-fields">
              {amenityMasterList && <Form.Item
                label="Amenities"
                name="selectedAmenities"
              >
                {/* {JSON.stringify(form.getFieldsValue())} */}
                <Checkbox.Group
                  disabled={disabled}
                  value={selectedAmenities}
                  className="amenity-checkbox"
                  options={getAllAmenities()}
                  onChange={onAmenityCheckBoxChange}
                />
              </Form.Item>}

            </Col>
            <Col md={14} className="edit-room-right-fields">
              <Row gutter={GUTTOR_SPACE_SMALL}>
                <Col md={24}>
                  <Form.Item
                    label="Room name"
                    name="roomAlias"
                    rules={[{ required: true, max: 30, message: 'Please enter valid room name' }]}
                  >
                    <Input placeholder="Room name" disabled={disabled}/>
                  </Form.Item>
                </Col>
                <Col md={24}>
                  <Form.Item
                    label="Room type"
                    name="typeName"
                    rules={[{ required: true, message: 'Please enter valid type name' }]}
                  >
                    <Select
                      className="w-100"
                      placeholder="Search"
                      optionLabelProp="typeName"
                      allowClear={true}
                      onChange={onRoomtypeChange}
                      options={roomType}
                      disabled={disabled}
                    />
                  </Form.Item>
                </Col>
                <Col md={12}>
                  <Form.Item
                    label="Rooms"
                    name="roomCount"
                    rules={[{ required: true, message: 'Please enter valid Rooms count' }]}
                  >
                    <InputNumber disabled={disabled} className="w-100" min={1} minLength={1} maxLength={4} placeholder="Rooms" />
                  </Form.Item>
                </Col>
                <Col md={12}>
                  <Form.Item
                    label="Beds per room"
                    name="bedsCountPerRoom"
                    rules={[{ required: true, message: 'Please enter valid Beds per room' }]}
                  >
                    <InputNumber disabled={disabled} className="w-100" min={1} width={'100%'} minLength={1} maxLength={4} placeholder="Beds per room" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          {
            !disabled &&
            <div className="text-right">
              <Button type="primary" htmlType={'submit'}>{room ? 'Update' : 'Save'}</Button>
            </div>
          }
        </Form>
      </FormModal>
    </>
  );
};

export default EditRoom;
